package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.AnimationAst
import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.DerivedAst
import top.wcpe.allincore.dsl.compiler.ast.DslHeaderAst
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.FontStyleAst
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.ast.HudLayoutAst
import top.wcpe.allincore.dsl.compiler.ast.HudStackAst
import top.wcpe.allincore.dsl.compiler.ast.HudVisibilityAst
import top.wcpe.allincore.dsl.compiler.ast.InterceptAst
import top.wcpe.allincore.dsl.compiler.ast.KeyBindingAst
import top.wcpe.allincore.dsl.compiler.ast.ModelAst
import top.wcpe.allincore.dsl.compiler.ast.NamespacedStateAst
import top.wcpe.allincore.dsl.compiler.ast.PapiBlockAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenKind
import top.wcpe.allincore.dsl.compiler.ast.StateAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.StyleRuleAst
import top.wcpe.allincore.dsl.compiler.ast.SubscribeBlockAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.diag.MigrationConfidence
import top.wcpe.allincore.dsl.compiler.diag.MigrationHint
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.ir.model.action.ReservedActions
import top.wcpe.allincore.dsl.ir.model.hud.HudMetaDefaults

/**
 * 解析顶层 `screen { ... }` 块，screenId 由编译入口按资源路径提供。
 *
 * Stage 11：通过 [kind] 复用相同解析逻辑解析 `hud { ... }`：
 * - [ScreenKind.SCREEN]：默认行为，期望 `screen` 关键字
 * - [ScreenKind.HUD]：期望 `hud` 关键字；与 screen 在 DSL 语法层完全等价
 */
internal class ScreenParser(
    private val p: Parser,
    private val kind: ScreenKind = ScreenKind.SCREEN,
    private val dslHeader: DslHeaderAst? = null,
) {

    companion object {
        /** `suspend` 是软关键字，词法层仍为 IDENTIFIER（与 TypeParser 约定一致）。 */
        private const val SOFT_KW_SUSPEND = "suspend"

        /** `onKey` 软关键字，screen 块顶层声明键盘事件 lambda。 */
        private const val SOFT_KW_ON_KEY = "onKey"

        /**
         * `onKeyRelease` 软关键字，screen 块顶层声明键盘松开事件 lambda。
         * 与 [SOFT_KW_ON_KEY] 对称：onKey 仅 PRESS、onKeyRelease 仅 RELEASE。
         */
        private const val SOFT_KW_ON_KEY_RELEASE = "onKeyRelease"

        /**
         * RFC-INPUT-EVENT-UNIFICATION Stage 4：`onMouseDown` 软关键字，
         * screen 块顶层声明全局鼠标按下事件 lambda。与 [SOFT_KW_ON_KEY] 对称。
         */
        private const val SOFT_KW_ON_MOUSE_DOWN = "onMouseDown"

        /**
         * RFC-MOUSE-EXTENDED-EVENTS Stage A：`onMouseUp` 软关键字，
         * screen 块顶层声明全局鼠标释放事件 lambda。与 [SOFT_KW_ON_MOUSE_DOWN] 对称。
         */
        private const val SOFT_KW_ON_MOUSE_UP = "onMouseUp"

        /**
         * RFC-MOUSE-EXTENDED-EVENTS Stage B：`onMouseMove` 软关键字，
         * screen 块顶层声明全局鼠标移动事件 lambda。与 [SOFT_KW_ON_MOUSE_UP] 对称。
         */
        private const val SOFT_KW_ON_MOUSE_MOVE = "onMouseMove"

        /** Stage 1：`open` 软关键字，screen 级生命周期 open 块。 */
        private const val SOFT_KW_OPEN = "open"

        /** Stage 1：`close` 软关键字，screen 级生命周期 close 块。 */
        private const val SOFT_KW_CLOSE = "close"

        /** `show` 软关键字：每次界面显示（首次 + 常驻重开）触发的生命周期块。 */
        private const val SOFT_KW_SHOW = "show"

        /** `onHide` 软关键字：每次界面隐藏（常驻收起）触发的生命周期块。
         *  （注意：`hide` 已被 HUD meta 的隐藏 token 列表 `hide = [...]` 占用，故用 onHide。） */
        private const val SOFT_KW_ON_HIDE = "onHide"

        /** Stage B：`subscribe` 软关键字，screen 级订阅块。 */
        private const val SOFT_KW_SUBSCRIBE = "subscribe"

        /** Stage 11+：hud 顶层 anchor 软关键字，整 HUD 屏幕锚点。 */
        private const val SOFT_KW_ANCHOR = "anchor"

        /** Stage 11+：hud 顶层 layer 软关键字，HUD 渲染层级。 */
        private const val SOFT_KW_LAYER = "layer"

        /** Stage 11+：hud 顶层 hide 软关键字，隐藏的 vanilla HUD token 列表。 */
        private const val SOFT_KW_HIDE = "hide"

        private const val SOFT_KW_Z_INDEX = "zIndex"
        private const val SOFT_KW_LAYOUT = "layout"
        private const val SOFT_KW_STACK = "stack"
        private const val SOFT_KW_VISIBILITY = "visibility"
        private const val SOFT_KW_TICK = "tick"

        /** 界面级默认字体软关键字，screen / hud 顶层 `font { ... }` 块。 */
        private const val SOFT_KW_FONT = "font"

        /** Phase 3：intercept 路由拦截块软关键字。 */
        private const val SOFT_KW_INTERCEPT = "intercept"

        /** Phase 4：HUD keybinding 按键绑定声明块软关键字。 */
        private const val SOFT_KW_KEYBINDING = "keybinding"

        /** 编译期 style-class 样式层：screen 顶层 `styles { ... }` 块软关键字。 */
        private const val SOFT_KW_STYLES = "styles"

        /** v2 数据模型块软关键字。 */
        private const val SOFT_KW_MODEL = "model"
        private const val SOFT_KW_MODIFIER_BUNDLES = "modifierBundles"

        /** Stage 11+：hud layer 合法取值闭集；非法值在 parser 阶段抛 E649。 */
        private val ALLOWED_HUD_LAYERS: Set<String> = HudMetaDefaults.ALLOWED_LAYERS

        /** Stage 11+：hud hide 合法 token 闭集；非法值在 parser 阶段丢弃。 */
        private val ALLOWED_HUD_HIDE_TOKENS: Set<String> = HudMetaDefaults.ALLOWED_HIDE_TOKENS

        private val LOWER_CAMEL_NAMESPACE = Regex("^[a-z][A-Za-z0-9]*$")

        private val ALLOWED_HUD_STACK_DIRECTIONS: Set<String> = HudMetaDefaults.ALLOWED_STACK_DIRECTIONS
        private val ALLOWED_HUD_VISIBILITY_MODES: Set<String> = HudMetaDefaults.ALLOWED_VISIBILITY_MODES
    }

    fun parseScreen(): ScreenAst {
        p.skipNewlines()
        // Stage 11：根据 kind 选择期望的关键字与对应错误信息
        val (expectedTok, missingMsg, dupRootMsg) = if (kind == ScreenKind.HUD) {
            Triple(TokenType.KW_HUD, "缺少 hud 关键字", "hud 必须包含 root 块")
        } else {
            Triple(TokenType.KW_SCREEN, "缺少 screen 关键字", "screen 必须包含 root 块")
        }
        val screenTok = p.expect(expectedTok, missingMsg)
        if (p.check(TokenType.STRING)) {
            val name = if (kind == ScreenKind.HUD) "hud" else "screen"
            p.fail("$name 后不再允许声明字符串 id；请使用资源路径作为 ${name}Id")
        }
        val headHint = if (kind == ScreenKind.HUD) "hud 头部后缺少 '{'" else "screen 头部后缺少 '{'"
        p.expect(TokenType.LBRACE, headHint)
        p.skipNewlines()

        // C4：预扫描整段 token，提取所有 screen 级（depth==1）的 `fun NAME` 名字
        // 写入 Parser.screenFuns，供 ExpressionParser 在解析阶段识别 forward reference。
        prescanScreenFuns()

        var state: StateAst? = null
        var sessionState: StateAst? = null
        var persistState: StateAst? = null
        var derived: DerivedAst? = null
        var model: ModelAst? = null
        val dslMode = dslHeader?.mode ?: DslMode.LEGACY
        val animations = mutableListOf<AnimationAst>()
        val funs = mutableListOf<FunDecl>()
        val inlineComponents = mutableListOf<ComponentAst>()
        val papiBlocks = mutableListOf<PapiBlockAst>()
        val subscribeBlocks = mutableListOf<SubscribeBlockAst>()
        var root: ElementAst? = null
        var theme: ThemeAst? = null
        val modifierBundles = mutableListOf<top.wcpe.allincore.dsl.compiler.ast.ModifierBundleAst>()
        // 编译期 style-class 样式层：screen 顶层 styles {} 块（只能声明一次）。
        var styles: List<StyleRuleAst>? = null
        var onKeyLambda: ExprAst.LambdaExpr? = null
        var onKeyReleaseLambda: ExprAst.LambdaExpr? = null
        var onMouseDownLambda: ExprAst.LambdaExpr? = null
        var onMouseUpLambda: ExprAst.LambdaExpr? = null
        var onMouseMoveLambda: ExprAst.LambdaExpr? = null
        var openBody: List<StmtAst>? = null
        var closeBody: List<StmtAst>? = null
        var showBody: List<StmtAst>? = null
        var hideBody: List<StmtAst>? = null
        // Stage 11+：hud 顶层 meta 声明（仅 hud 块允许）
        var hudAnchor: String? = null
        var hudLayer: String? = null
        var hudHideTokens: List<String>? = null
        var hudZIndex: Int? = null
        var hudLayout: HudLayoutAst? = null
        var hudStack: HudStackAst? = null
        var hudVisibility: HudVisibilityAst? = null
        var hudTickBody: List<StmtAst>? = null
        var screenFont: FontStyleAst? = null
        // Phase 3：intercept 路由拦截块
        var interceptAst: InterceptAst? = null
        // Phase 4：HUD keybinding 按键绑定声明列表
        val hudKeyBindings = mutableListOf<KeyBindingAst>()

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            when (p.current().type) {
                TokenType.KW_STATE -> {
                    rejectLegacyInStrict("state defaults", dslMode)
                    if (state != null) p.fail("state defaults 块只能声明一次")
                    state = StateBlockParser(p).parse()
                }
                TokenType.KW_SESSION -> {
                    rejectLegacyInStrict("session defaults", dslMode)
                    if (sessionState != null) p.fail("session defaults 块只能声明一次")
                    sessionState = StateBlockParser(p).parseScopedDefaults(TokenType.KW_SESSION, "session")
                }
                TokenType.KW_PERSIST -> {
                    rejectLegacyInStrict("persist defaults", dslMode)
                    if (persistState != null) p.fail("persist defaults 块只能声明一次")
                    persistState = StateBlockParser(p).parseScopedDefaults(TokenType.KW_PERSIST, "persist")
                }
                TokenType.KW_DERIVED -> {
                    rejectLegacyInStrict("derived defaults", dslMode)
                    if (derived != null) p.fail("derived defaults 块只能声明一次")
                    derived = StateBlockParser(p).parseDerived()
                }
                TokenType.KW_ANIMATION -> {
                    animations += AnimationBlockParser(p).parse()
                }
                TokenType.KW_FUN -> {
                    funs += FunDeclParser(p).parseScreenLevel()
                }
                TokenType.KW_COMPONENT -> {
                    inlineComponents += InlineComponentParser(p).parse()
                }
                TokenType.KW_PAPI -> {
                    papiBlocks += PapiBlockParser(p).parse()
                }
                TokenType.KW_ROOT -> {
                    if (root != null) p.fail("root 块只能声明一次")
                    root = ElementBlockParser(p, dslMode).parseRoot()
                }
                TokenType.KW_THEME -> {
                    if (theme != null) p.fail("theme 块只能声明一次")
                    theme = ThemeBlockParser(p, dslMode).parse()
                }
                TokenType.IDENTIFIER -> {
                    val cur = p.current()
                    when (cur.lexeme) {
                        // M5-e：识别 `suspend fun` 前缀（suspend 为软关键字）
                        SOFT_KW_SUSPEND -> {
                            if (p.peek(1).type == TokenType.KW_FUN) {
                                p.advance() // 消费 suspend
                                funs += FunDeclParser(p).parseScreenLevel(isSuspend = true)
                            } else {
                                p.fail(blockKindHint("不识别的 token: '${cur.lexeme}'"))
                            }
                        }
                        SOFT_KW_MODEL -> {
                            if (dslMode == DslMode.LEGACY) {
                                p.fail(DslError.AUI_V2_LEGACY_FORBIDS_V2_SYNTAX, cur.position, "model")
                            }
                            if (model != null) p.fail("model 块只能声明一次", cur.position)
                            if (state != null || sessionState != null || persistState != null || derived != null) {
                                p.fail(
                                    DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX,
                                    cur.position,
                                    "model 与 legacy state 入口混用",
                                )
                            }
                            val parsedModel = ModelBlockParser(p).parse(component = false)
                            model = parsedModel
                            state = parsedModel.state
                            sessionState = flattenNamespaces(parsedModel.sessionNamespaces, "session")
                            persistState = flattenNamespaces(parsedModel.persistNamespaces, "persist")
                            derived = parsedModel.derived
                        }
                        SOFT_KW_MODIFIER_BUNDLES -> {
                            if (dslMode == DslMode.LEGACY) {
                                p.fail(DslError.AUI_V2_LEGACY_FORBIDS_V2_SYNTAX, cur.position, "modifierBundles")
                            }
                            modifierBundles += ModifierBundleParser(p).parse()
                        }
                        // screen / hud 级 onKey 事件 lambda
                        SOFT_KW_ON_KEY -> {
                            if (onKeyLambda != null) p.fail("onKey 块只能声明一次", cur.position)
                            onKeyLambda = parseOnKeyLambda()
                        }
                        // screen / hud 级 onKeyRelease 松开事件 lambda
                        SOFT_KW_ON_KEY_RELEASE -> {
                            if (onKeyReleaseLambda != null) p.fail("onKeyRelease 块只能声明一次", cur.position)
                            onKeyReleaseLambda = parseOnKeyReleaseLambda()
                        }
                        // RFC Stage 4：screen / hud 级 onMouseDown 全局鼠标按下事件 lambda
                        SOFT_KW_ON_MOUSE_DOWN -> {
                            if (onMouseDownLambda != null) p.fail("onMouseDown 块只能声明一次", cur.position)
                            onMouseDownLambda = parseOnMouseDownLambda()
                        }
                        // RFC-MOUSE-EXTENDED-EVENTS Stage A：screen / hud 级 onMouseUp 全局鼠标释放事件 lambda
                        SOFT_KW_ON_MOUSE_UP -> {
                            if (onMouseUpLambda != null) p.fail("onMouseUp 块只能声明一次", cur.position)
                            onMouseUpLambda = parseOnMouseUpLambda()
                        }
                        // RFC-MOUSE-EXTENDED-EVENTS Stage B：screen / hud 级 onMouseMove 全局鼠标移动事件 lambda
                        SOFT_KW_ON_MOUSE_MOVE -> {
                            if (onMouseMoveLambda != null) p.fail("onMouseMove 块只能声明一次", cur.position)
                            onMouseMoveLambda = parseOnMouseMoveLambda()
                        }
                        // Stage 1：screen 级 open 生命周期块
                        SOFT_KW_OPEN -> {
                            if (root != null) {
                                p.fail(DslError.E647_LIFECYCLE_AFTER_ROOT, cur.position, SOFT_KW_OPEN)
                            }
                            if (openBody != null) {
                                p.fail(DslError.E646_LIFECYCLE_DUPLICATE, cur.position, SOFT_KW_OPEN)
                            }
                            openBody = OpenCloseBlockParser(p).parseOpen()
                        }
                        // Stage 1：screen 级 close 生命周期块
                        SOFT_KW_CLOSE -> {
                            if (root != null) {
                                p.fail(DslError.E647_LIFECYCLE_AFTER_ROOT, cur.position, SOFT_KW_CLOSE)
                            }
                            if (closeBody != null) {
                                p.fail(DslError.E646_LIFECYCLE_DUPLICATE, cur.position, SOFT_KW_CLOSE)
                            }
                            closeBody = OpenCloseBlockParser(p).parseClose()
                        }
                        // show 生命周期块：每次界面显示触发（首次 + 常驻重开）
                        SOFT_KW_SHOW -> {
                            if (root != null) {
                                p.fail(DslError.E647_LIFECYCLE_AFTER_ROOT, cur.position, SOFT_KW_SHOW)
                            }
                            if (showBody != null) {
                                p.fail(DslError.E646_LIFECYCLE_DUPLICATE, cur.position, SOFT_KW_SHOW)
                            }
                            showBody = OpenCloseBlockParser(p).parseShow()
                        }
                        // onHide 生命周期块：每次界面隐藏触发（常驻收起）
                        SOFT_KW_ON_HIDE -> {
                            if (root != null) {
                                p.fail(DslError.E647_LIFECYCLE_AFTER_ROOT, cur.position, SOFT_KW_ON_HIDE)
                            }
                            if (hideBody != null) {
                                p.fail(DslError.E646_LIFECYCLE_DUPLICATE, cur.position, SOFT_KW_ON_HIDE)
                            }
                            hideBody = OpenCloseBlockParser(p).parseHide()
                        }
                        // Stage B：screen 级 subscribe 订阅块
                        SOFT_KW_SUBSCRIBE -> {
                            subscribeBlocks += SubscribeBlockParser(p).parse()
                        }
                        // Stage 11+：hud 顶层 anchor / layer / hide 声明（仅 hud 块允许）
                        SOFT_KW_ANCHOR -> {
                            if (kind != ScreenKind.HUD) {
                                p.fail(DslError.E650_HUD_PROP_OUTSIDE_HUD, cur.position, SOFT_KW_ANCHOR)
                            }
                            if (hudAnchor != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_ANCHOR)
                            }
                            hudAnchor = parseHudStringProp(SOFT_KW_ANCHOR)
                        }
                        SOFT_KW_LAYER -> {
                            if (kind != ScreenKind.HUD) {
                                p.fail(DslError.E650_HUD_PROP_OUTSIDE_HUD, cur.position, SOFT_KW_LAYER)
                            }
                            if (hudLayer != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_LAYER)
                            }
                            // 先消费 IDENT + `=`，再读取 STRING token；STRING token 的位置用于精确报错
                            p.advance() // 消费 layer 标识符
                            p.expect(TokenType.EQUALS, "hud layer 后缺少 '='")
                            val layerTok = p.expect(TokenType.STRING, "hud layer 必须为字符串字面量")
                            val layerValue = layerTok.literalValue as String
                            if (layerValue !in ALLOWED_HUD_LAYERS) {
                                p.fail(DslError.E649_INVALID_HUD_LAYER, layerTok.position, layerValue)
                            }
                            hudLayer = layerValue
                        }
                        SOFT_KW_HIDE -> {
                            ensureHudProp(cur.lexeme)
                            if (hudHideTokens != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_HIDE)
                            }
                            hudHideTokens = parseHudStringList(SOFT_KW_HIDE)
                        }
                        SOFT_KW_Z_INDEX -> {
                            ensureHudProp(cur.lexeme)
                            if (hudZIndex != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_Z_INDEX)
                            }
                            hudZIndex = parseHudIntProp(SOFT_KW_Z_INDEX)
                        }
                        SOFT_KW_LAYOUT -> {
                            ensureHudProp(cur.lexeme)
                            if (hudLayout != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_LAYOUT)
                            }
                            hudLayout = parseHudLayoutBlock()
                        }
                        SOFT_KW_STACK -> {
                            ensureHudProp(cur.lexeme)
                            if (hudStack != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_STACK)
                            }
                            hudStack = parseHudStackBlock()
                        }
                        SOFT_KW_VISIBILITY -> {
                            ensureHudProp(cur.lexeme)
                            if (hudVisibility != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_VISIBILITY)
                            }
                            hudVisibility = parseHudVisibilityBlock()
                        }
                        SOFT_KW_TICK -> {
                            ensureHudProp(cur.lexeme)
                            if (hudTickBody != null) {
                                p.fail(DslError.E648_HUD_PROP_DUPLICATE, cur.position, SOFT_KW_TICK)
                            }
                            hudTickBody = parseHudTickBlock()
                        }
                        // 界面级默认字体块 `font { ... }`（screen / hud 均可，作用于本界面所有文字）
                        SOFT_KW_FONT -> {
                            if (screenFont != null) p.fail("font 块只能声明一次", cur.position)
                            screenFont = FontBlockParser(p).parse()
                        }
                        // 编译期 style-class 样式层：screen 顶层 styles { ... } 块
                        SOFT_KW_STYLES -> {
                            rejectLegacyInStrict("styles", dslMode)
                            p.warnMigration(
                                DslError.AUI_V2_LEGACY_DEPRECATED_SYNTAX,
                                cur.position,
                                MigrationHint("modifierBundles / modifiers", MigrationConfidence.MANUAL),
                                "styles",
                                "modifierBundles / modifiers",
                            )
                            if (styles != null) p.fail("styles 块只能声明一次", cur.position)
                            styles = StylesBlockParser(p).parse()
                        }
                        // Phase 3：intercept 路由拦截块
                        SOFT_KW_INTERCEPT -> {
                            if (interceptAst != null) {
                                p.fail(blockKindHint("intercept 块只能声明一次"))
                            }
                            interceptAst = parseInterceptBlock()
                        }
                        // Phase 4：HUD keybinding 按键绑定声明块
                        SOFT_KW_KEYBINDING -> {
                            ensureHudProp(cur.lexeme)
                            hudKeyBindings.addAll(parseHudKeybindingBlock())
                        }
                        else -> p.fail(blockKindHint("不识别的 token: '${cur.lexeme}'"))
                    }
                }
                else -> {
                    val cur = p.current()
                    p.fail(blockKindHint("不识别的 token: '${cur.lexeme}'"))
                }
            }
            p.skipNewlines()
        }

        val closeHint = if (kind == ScreenKind.HUD) "hud 块缺少 '}'" else "screen 块缺少 '}'"
        val closeToken = p.expect(TokenType.RBRACE, closeHint)
        p.skipNewlines()

        if (root == null) p.fail(dupRootMsg, screenTok.position)

        return ScreenAst(
            id = p.screenId,
            state = state,
            dslHeader = dslHeader,
            model = model,
            sessionState = sessionState,
            persistState = persistState,
            derived = derived,
            root = root,
            theme = theme,
            animations = animations,
            funs = funs,
            onKeyLambda = onKeyLambda,
            onKeyReleaseLambda = onKeyReleaseLambda,
            onMouseDownLambda = onMouseDownLambda,
            onMouseUpLambda = onMouseUpLambda,
            onMouseMoveLambda = onMouseMoveLambda,
            inlineComponents = inlineComponents,
            openBody = openBody,
            closeBody = closeBody,
            showBody = showBody,
            hideBody = hideBody,
            papiBlocks = papiBlocks,
            subscribeBlocks = subscribeBlocks,
            kind = kind,
            hudAnchor = hudAnchor,
            hudLayer = hudLayer,
            hudHideTokens = hudHideTokens.orEmpty(),
            hudZIndex = hudZIndex ?: 0,
            hudLayout = hudLayout,
            hudStack = hudStack,
            hudVisibility = hudVisibility,
            hudTickBody = hudTickBody,
            screenFont = screenFont,
            intercept = interceptAst,
            hudKeyBindings = hudKeyBindings,
            styles = styles.orEmpty(),
            modifierBundles = modifierBundles,
            position = p.completePosition(screenTok.position, closeToken),
        )
    }

    private fun rejectLegacyInStrict(name: String, mode: DslMode) {
        if (mode == DslMode.STRICT) {
            p.fail(DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX, p.current().position, name)
        }
    }

    private fun flattenNamespaces(namespaces: List<NamespacedStateAst>, scopeName: String): StateAst? {
        if (namespaces.isEmpty()) return null
        val entries = mutableListOf<StateAst.StateEntry>()
        for (namespace in namespaces) {
            if (!LOWER_CAMEL_NAMESPACE.matches(namespace.namespace)) {
                p.fail(DslError.AUI_V2_INVALID_NAMESPACE, namespace.position, scopeName, namespace.namespace)
            }
            entries += namespace.state.entries.map { entry ->
                entry.copy(name = "${namespace.namespace}.${entry.name}")
            }
        }
        return StateAst(entries, namespaces.first().state.position)
    }

    private fun ensureHudProp(propName: String) {
        if (kind != ScreenKind.HUD) {
            p.fail(DslError.E650_HUD_PROP_OUTSIDE_HUD, p.current().position, propName)
        }
    }

    /** 根据 kind 生成 "screen 块内 ..." / "hud 块内 ..." 错误前缀。 */
    private fun blockKindHint(suffix: String): String {
        val name = if (kind == ScreenKind.HUD) "hud" else "screen"
        return "$name 块内$suffix"
    }

    /**
     * 解析 `onKey { event -> ... }`：消费 `onKey` 标识符后调用 ExpressionParser 解析独立 lambda。
     *
     * 复用 ActionBlockParser 提供的 ExpressionParser，使 lambda 体内可使用
     * if / var / state.x = ... / local.close() 等所有 action 体支持的语法。
     * 校验 lambda 必须有且仅有 1 个参数，且形参名必须为 `event`。
     */
    private fun parseOnKeyLambda(): ExprAst.LambdaExpr {
        p.advance() // 消费 onKey 标识符
        p.skipNewlines()
        val exprParser = ActionBlockParser(p).expressionParser(allowBareIdent = false)
        val lambda = exprParser.parseStandaloneLambda()
        if (lambda.params.size != 1) {
            p.fail(
                "onKey lambda 必须接收 1 个参数（必须命名为 ${ReservedActions.SCREEN_ON_KEY_PARAM}），实际 ${lambda.params.size} 个",
                lambda.position,
            )
        }
        if (lambda.params.first() != ReservedActions.SCREEN_ON_KEY_PARAM) {
            p.fail(
                "onKey lambda 形参名必须为 '${ReservedActions.SCREEN_ON_KEY_PARAM}'，实际: '${lambda.params.first()}'",
                lambda.position,
            )
        }
        return lambda
    }

    /**
     * 解析 `onKeyRelease { event -> ... }`：与 [parseOnKeyLambda] 严格镜像。
     *
     * 仅在玩家松开按键(GLFW RELEASE,action=0)时由平台层调用,与 onKey 的 PRESS
     * 语义分离。lambda 体可访问 `event.holdMs` 拿到按下时长(毫秒)。
     */
    private fun parseOnKeyReleaseLambda(): ExprAst.LambdaExpr {
        p.advance() // 消费 onKeyRelease 标识符
        p.skipNewlines()
        val exprParser = ActionBlockParser(p).expressionParser(allowBareIdent = false)
        val lambda = exprParser.parseStandaloneLambda()
        if (lambda.params.size != 1) {
            p.fail(
                "onKeyRelease lambda 必须接收 1 个参数（必须命名为 ${ReservedActions.SCREEN_ON_KEY_RELEASE_PARAM}），实际 ${lambda.params.size} 个",
                lambda.position,
            )
        }
        if (lambda.params.first() != ReservedActions.SCREEN_ON_KEY_RELEASE_PARAM) {
            p.fail(
                "onKeyRelease lambda 形参名必须为 '${ReservedActions.SCREEN_ON_KEY_RELEASE_PARAM}'，实际: '${lambda.params.first()}'",
                lambda.position,
            )
        }
        return lambda
    }

    /**
     * RFC-INPUT-EVENT-UNIFICATION Stage 4：解析 `onMouseDown { event -> ... }`。
     *
     * 与 [parseOnKeyLambda] 完全镜像：消费 `onMouseDown` 标识符后调用 ExpressionParser
     * 解析独立 lambda；校验 lambda 必须有且仅有 1 个参数，且形参名必须为 `event`。
     */
    private fun parseOnMouseDownLambda(): ExprAst.LambdaExpr {
        p.advance() // 消费 onMouseDown 标识符
        p.skipNewlines()
        val exprParser = ActionBlockParser(p).expressionParser(allowBareIdent = false)
        val lambda = exprParser.parseStandaloneLambda()
        if (lambda.params.size != 1) {
            p.fail(
                "onMouseDown lambda 必须接收 1 个参数（必须命名为 ${ReservedActions.SCREEN_ON_MOUSE_DOWN_PARAM}），实际 ${lambda.params.size} 个",
                lambda.position,
            )
        }
        if (lambda.params.first() != ReservedActions.SCREEN_ON_MOUSE_DOWN_PARAM) {
            p.fail(
                "onMouseDown lambda 形参名必须为 '${ReservedActions.SCREEN_ON_MOUSE_DOWN_PARAM}'，实际: '${lambda.params.first()}'",
                lambda.position,
            )
        }
        return lambda
    }

    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage A：解析 `onMouseUp { event -> ... }`。
     *
     * 与 [parseOnMouseDownLambda] 完全镜像：消费 `onMouseUp` 标识符后调用 ExpressionParser
     * 解析独立 lambda；校验 lambda 必须有且仅有 1 个参数，且形参名必须为 `event`。
     */
    private fun parseOnMouseUpLambda(): ExprAst.LambdaExpr {
        p.advance() // 消费 onMouseUp 标识符
        p.skipNewlines()
        val exprParser = ActionBlockParser(p).expressionParser(allowBareIdent = false)
        val lambda = exprParser.parseStandaloneLambda()
        if (lambda.params.size != 1) {
            p.fail(
                "onMouseUp lambda 必须接收 1 个参数（必须命名为 ${ReservedActions.SCREEN_ON_MOUSE_UP_PARAM}），实际 ${lambda.params.size} 个",
                lambda.position,
            )
        }
        if (lambda.params.first() != ReservedActions.SCREEN_ON_MOUSE_UP_PARAM) {
            p.fail(
                "onMouseUp lambda 形参名必须为 '${ReservedActions.SCREEN_ON_MOUSE_UP_PARAM}'，实际: '${lambda.params.first()}'",
                lambda.position,
            )
        }
        return lambda
    }

    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage B：解析 `onMouseMove { event -> ... }`。
     *
     * 与 [parseOnMouseUpLambda] 完全镜像：消费 `onMouseMove` 标识符后调用 ExpressionParser
     * 解析独立 lambda；校验 lambda 必须有且仅有 1 个参数，且形参名必须为 `event`。
     */
    private fun parseOnMouseMoveLambda(): ExprAst.LambdaExpr {
        p.advance() // 消费 onMouseMove 标识符
        p.skipNewlines()
        val exprParser = ActionBlockParser(p).expressionParser(allowBareIdent = false)
        val lambda = exprParser.parseStandaloneLambda()
        if (lambda.params.size != 1) {
            p.fail(
                "onMouseMove lambda 必须接收 1 个参数（必须命名为 ${ReservedActions.SCREEN_ON_MOUSE_MOVE_PARAM}），实际 ${lambda.params.size} 个",
                lambda.position,
            )
        }
        if (lambda.params.first() != ReservedActions.SCREEN_ON_MOUSE_MOVE_PARAM) {
            p.fail(
                "onMouseMove lambda 形参名必须为 '${ReservedActions.SCREEN_ON_MOUSE_MOVE_PARAM}'，实际: '${lambda.params.first()}'",
                lambda.position,
            )
        }
        return lambda
    }

    /**
     * Stage 11+：解析 hud 顶层 `xxx = "string"` 声明，返回字符串值。
     *
     * 期望当前 token 为 IDENT(xxx)，依次消费 IDENT + `=` + STRING 三个 token。
     * 仅供 hud anchor 使用；layer 走自己的内联逻辑（错误位置精度更高），hide 走 [parseHudStringList]。
     */
    private fun parseHudStringProp(propName: String): String {
        p.advance() // 消费 propName 标识符
        p.expect(TokenType.EQUALS, "hud $propName 后缺少 '='")
        val tok = p.expect(TokenType.STRING, "hud $propName 必须为字符串字面量")
        return tok.literalValue as String
    }

    /**
     * Stage 11+：解析 hud 顶层字符串列表声明。
     *
     * 期望当前 token 为 IDENT(name)，依次消费 IDENT + `=` + LBRACKET + 字符串列表 + RBRACKET。
     */
    private fun parseHudStringList(propName: String): List<String> {
        p.advance()
        p.expect(TokenType.EQUALS, "hud $propName 后缺少 '='")
        return parseHudStringListValue(propName)
    }

    private fun parseHudStringListValue(propName: String): List<String> {
        p.expect(TokenType.LBRACKET, "hud $propName 必须为字符串列表（以 '[' 开头）")
        p.skipNewlines()
        val items = mutableListOf<String>()
        if (!p.check(TokenType.RBRACKET)) {
            items += parseHudListStringItem(propName)
            p.skipNewlines()
            while (p.check(TokenType.COMMA)) {
                p.advance()
                p.skipNewlines()
                if (p.check(TokenType.RBRACKET)) break
                items += parseHudListStringItem(propName)
                p.skipNewlines()
            }
        }
        p.expect(TokenType.RBRACKET, "hud $propName 列表缺少 ']'")
        return if (propName == SOFT_KW_HIDE) items.filter { it in ALLOWED_HUD_HIDE_TOKENS } else items
    }

    private fun parseHudListStringItem(propName: String): String {
        val tok = p.expect(TokenType.STRING, "hud $propName 列表元素必须为字符串字面量")
        return tok.literalValue as String
    }

    private fun parseHudIntProp(propName: String): Int {
        p.advance()
        p.expect(TokenType.EQUALS, "hud $propName 后缺少 '='")
        return parseHudIntValue(propName)
    }

    private fun parseHudIntValue(propName: String): Int {
        val sign = if (p.check(TokenType.MINUS)) {
            p.advance()
            -1
        } else {
            1
        }
        val tok = p.expect(TokenType.NUMBER, "hud $propName 必须为数字字面量")
        val value = (tok.literalValue as Number).toDouble()
        if (value % 1.0 != 0.0) {
            p.fail("hud $propName 必须为整数", tok.position)
        }
        return sign * value.toInt()
    }

    private fun parseHudBoolValue(propName: String): Boolean {
        val tok = p.expect(TokenType.BOOLEAN, "hud $propName 必须为布尔字面量")
        return tok.literalValue as Boolean
    }

    private fun parseHudLayoutBlock(): HudLayoutAst {
        p.advance()
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "hud layout 后缺少 '{'")
        p.skipNewlines()
        var offsetX: Int? = null
        var offsetY: Int? = null
        var safeArea: Boolean? = null
        var avoidVanilla: Boolean? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val name = p.expect(TokenType.IDENTIFIER, "hud layout 块内必须为属性声明")
            p.expect(TokenType.EQUALS, "hud layout.${name.lexeme} 后缺少 '='")
            when (name.lexeme) {
                "offsetX" -> offsetX = parseUniqueIntField(offsetX, "layout.offsetX")
                "offsetY" -> offsetY = parseUniqueIntField(offsetY, "layout.offsetY")
                "safeArea" -> safeArea = parseUniqueBoolField(safeArea, "layout.safeArea")
                "avoidVanilla" -> avoidVanilla = parseUniqueBoolField(avoidVanilla, "layout.avoidVanilla")
                else -> p.fail("不识别的 hud layout 属性: '${name.lexeme}'", name.position)
            }
            p.skipNewlines()
        }
        p.expect(TokenType.RBRACE, "hud layout 块缺少 '}'")
        return HudLayoutAst(offsetX ?: 0, offsetY ?: 0, safeArea ?: true, avoidVanilla ?: true)
    }

    private fun parseHudStackBlock(): HudStackAst {
        p.advance()
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "hud stack 后缺少 '{'")
        p.skipNewlines()
        var group: String? = null
        var direction: String? = null
        var gap: Int? = null
        var order: Int? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val name = p.expect(TokenType.IDENTIFIER, "hud stack 块内必须为属性声明")
            p.expect(TokenType.EQUALS, "hud stack.${name.lexeme} 后缺少 '='")
            when (name.lexeme) {
                "group" -> group = parseUniqueStringField(group, "stack.group")
                "direction" -> {
                    direction = parseUniqueStringField(direction, "stack.direction")
                    if (direction !in ALLOWED_HUD_STACK_DIRECTIONS) {
                        p.fail("非法 hud stack.direction: '$direction'", name.position)
                    }
                }
                "gap" -> gap = parseUniqueIntField(gap, "stack.gap")
                "order" -> order = parseUniqueIntField(order, "stack.order")
                else -> p.fail("不识别的 hud stack 属性: '${name.lexeme}'", name.position)
            }
            p.skipNewlines()
        }
        p.expect(TokenType.RBRACE, "hud stack 块缺少 '}'")
        return HudStackAst(group, direction ?: HudMetaDefaults.STACK_DOWN, gap ?: 4, order ?: 0)
    }

    private fun parseHudVisibilityBlock(): HudVisibilityAst {
        p.advance()
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "hud visibility 后缺少 '{'")
        p.skipNewlines()
        var whenScreen: String? = null
        var allow: List<String>? = null
        var deny: List<String>? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val name = p.expect(TokenType.IDENTIFIER, "hud visibility 块内必须为属性声明")
            when (name.lexeme) {
                "whenScreen" -> {
                    p.expect(TokenType.EQUALS, "hud visibility.whenScreen 后缺少 '='")
                    whenScreen = parseUniqueStringField(whenScreen, "visibility.whenScreen")
                    if (whenScreen !in ALLOWED_HUD_VISIBILITY_MODES) {
                        p.fail("非法 hud visibility.whenScreen: '$whenScreen'", name.position)
                    }
                }
                "allow" -> {
                    if (allow != null) p.fail(DslError.E648_HUD_PROP_DUPLICATE, name.position, "visibility.allow")
                    p.expect(TokenType.EQUALS, "hud visibility.allow 后缺少 '='")
                    allow = parseHudStringListValue("allow")
                }
                "deny" -> {
                    if (deny != null) p.fail(DslError.E648_HUD_PROP_DUPLICATE, name.position, "visibility.deny")
                    p.expect(TokenType.EQUALS, "hud visibility.deny 后缺少 '='")
                    deny = parseHudStringListValue("deny")
                }
                else -> p.fail("不识别的 hud visibility 属性: '${name.lexeme}'", name.position)
            }
            p.skipNewlines()
        }
        p.expect(TokenType.RBRACE, "hud visibility 块缺少 '}'")
        return HudVisibilityAst(whenScreen ?: HudMetaDefaults.VISIBILITY_HIDE, allow.orEmpty(), deny.orEmpty())
    }

    private fun parseHudTickBlock(): List<StmtAst> {
        p.advance()
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "hud tick 后缺少 '{'")
        p.skipNewlines()
        val body = ActionBlockParser(p).parseLambdaBody(params = emptyList(), allowBareIdent = false)
        p.expect(TokenType.RBRACE, "hud tick 块缺少 '}'")
        return body
    }

    /**
     * Phase 3：解析 `intercept { menuType = "..."; when { ... } }` 块。
     *
     * - `menuType`：必填，字符串字面量
     * - `when`：可选，条件表达式块（编译为 ExprAst）
     */
    private fun parseInterceptBlock(): InterceptAst {
        val startPos = p.current().position
        p.advance() // 消费 intercept 标识符
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "intercept 后缺少 '{'")
        p.skipNewlines()
        var menuType: String? = null
        var condition: ExprAst? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val name = p.current()
            when (name.lexeme) {
                "menuType" -> {
                    if (menuType != null) p.fail("intercept.menuType 只能声明一次", name.position)
                    p.advance()
                    p.expect(TokenType.EQUALS, "intercept.menuType 后缺少 '='")
                    val tok = p.expect(TokenType.STRING, "intercept.menuType 必须为字符串字面量")
                    menuType = tok.literalValue as String
                    if (menuType.isBlank()) p.fail("intercept.menuType 不能为空字符串", tok.position)
                }
                "when" -> {
                    if (condition != null) p.fail("intercept.when 只能声明一次", name.position)
                    p.advance()
                    p.skipNewlines()
                    p.expect(TokenType.LBRACE, "intercept.when 后缺少 '{'")
                    p.skipNewlines()
                    val exprParser = ActionBlockParser(p).expressionParser(allowBareIdent = false)
                    condition = exprParser.parse()
                    p.skipNewlines()
                    p.expect(TokenType.RBRACE, "intercept.when 块缺少 '}'")
                }
                else -> p.fail("不识别的 intercept 属性: '${name.lexeme}'", name.position)
            }
            p.skipNewlines()
        }
        val close = p.expect(TokenType.RBRACE, "intercept 块缺少 '}'")
        val resolvedMenuType = menuType ?: p.fail("intercept 块必须声明 menuType", startPos)
        return InterceptAst(resolvedMenuType, condition, p.completePosition(startPos, close))
    }

    /**
     * Phase 4：解析 `keybinding { "id" { defaultKey = "P"; label = "..."; onTrigger { ... } } }` 块。
     *
     * 一个 keybinding 块包含多个条目，每个条目有 id + 属性 + onTrigger。
     */
    private fun parseHudKeybindingBlock(): List<KeyBindingAst> {
        p.advance() // 消费 keybinding 标识符
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "keybinding 后缺少 '{'")
        p.skipNewlines()
        val bindings = mutableListOf<KeyBindingAst>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val idTok = p.expect(TokenType.STRING, "keybinding 条目必须以字符串 id 开头")
            val id = idTok.literalValue as String
            if (id.isBlank()) p.fail("keybinding id 不能为空字符串", idTok.position)
            val startPos = idTok.position
            p.skipNewlines()
            p.expect(TokenType.LBRACE, "keybinding '$id' 后缺少 '{'")
            p.skipNewlines()
            var defaultKey: String? = null
            var label: String? = null
            var onTrigger: List<StmtAst>? = null
            while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
                val prop = p.current()
                when (prop.lexeme) {
                    "defaultKey" -> {
                        if (defaultKey != null) p.fail("keybinding.defaultKey 只能声明一次", prop.position)
                        p.advance()
                        p.expect(TokenType.EQUALS, "keybinding.defaultKey 后缺少 '='")
                        defaultKey = p.expect(TokenType.STRING, "keybinding.defaultKey 必须为字符串").literalValue as String
                    }
                    "label" -> {
                        if (label != null) p.fail("keybinding.label 只能声明一次", prop.position)
                        p.advance()
                        p.expect(TokenType.EQUALS, "keybinding.label 后缺少 '='")
                        label = p.expect(TokenType.STRING, "keybinding.label 必须为字符串").literalValue as String
                    }
                    "onTrigger" -> {
                        if (onTrigger != null) p.fail("keybinding.onTrigger 只能声明一次", prop.position)
                        p.advance()
                        p.skipNewlines()
                        p.expect(TokenType.LBRACE, "keybinding.onTrigger 后缺少 '{'")
                        p.skipNewlines()
                        onTrigger = ActionBlockParser(p).parseLambdaBody(params = emptyList(), allowBareIdent = false)
                        p.expect(TokenType.RBRACE, "keybinding.onTrigger 块缺少 '}'")
                    }
                    else -> p.fail("不识别的 keybinding 属性: '${prop.lexeme}'", prop.position)
                }
                p.skipNewlines()
            }
            val close = p.expect(TokenType.RBRACE, "keybinding '$id' 块缺少 '}'")
            val trigger = onTrigger ?: p.fail("keybinding '$id' 必须声明 onTrigger", startPos)
            bindings.add(KeyBindingAst(id, defaultKey, label, trigger, p.completePosition(startPos, close)))
            p.skipNewlines()
        }
        p.expect(TokenType.RBRACE, "keybinding 块缺少 '}'")
        return bindings
    }

    private fun parseUniqueIntField(current: Int?, propName: String): Int {
        if (current != null) p.fail(DslError.E648_HUD_PROP_DUPLICATE, p.current().position, propName)
        return parseHudIntValue(propName)
    }

    private fun parseUniqueBoolField(current: Boolean?, propName: String): Boolean {
        if (current != null) p.fail(DslError.E648_HUD_PROP_DUPLICATE, p.current().position, propName)
        return parseHudBoolValue(propName)
    }

    private fun parseUniqueStringField(current: String?, propName: String): String {
        if (current != null) p.fail(DslError.E648_HUD_PROP_DUPLICATE, p.current().position, propName)
        val tok = p.expect(TokenType.STRING, "hud $propName 必须为字符串字面量")
        return tok.literalValue as String
    }

    /**
     * 预扫 screen 块体（当前 `{` 已消费），把所有 depth==1 的 `fun NAME` 名字加入
     * [Parser.screenFuns]。仅做名字识别，不消费任何 token。
     *
     * 实现：从当前 index 起遍历 tokens，维护 brace 嵌套深度；当深度为 1 且遇到
     * `KW_FUN IDENTIFIER` 序列时记录名字。
     */
    private fun prescanScreenFuns() {
        var offset = 0
        var depth = 1
        while (true) {
            val t = p.peek(offset)
            when (t.type) {
                TokenType.LBRACE -> {
                    depth++
                }
                TokenType.RBRACE -> {
                    depth--
                    if (depth == 0) return
                }
                TokenType.KW_FUN -> {
                    if (depth == 1) {
                        // 期望紧随 IDENTIFIER
                        var k = offset + 1
                        while (p.peek(k).type == TokenType.NEWLINE) k++
                        if (p.peek(k).type == TokenType.IDENTIFIER) {
                            p.screenFuns += p.peek(k).lexeme
                        }
                    }
                }
                TokenType.IDENTIFIER -> {
                    // M5-e：兼容 `suspend fun NAME` 前缀（suspend 为软关键字）。
                    // 仅在 depth==1 时识别：下一个非 NEWLINE token 为 KW_FUN，再下一个为 IDENTIFIER。
                    if (depth == 1 && t.lexeme == SOFT_KW_SUSPEND) {
                        var k = offset + 1
                        while (p.peek(k).type == TokenType.NEWLINE) k++
                        if (p.peek(k).type == TokenType.KW_FUN) {
                            var j = k + 1
                            while (p.peek(j).type == TokenType.NEWLINE) j++
                            if (p.peek(j).type == TokenType.IDENTIFIER) {
                                p.screenFuns += p.peek(j).lexeme
                            }
                        }
                    }
                }
                TokenType.EOF -> return
                else -> Unit
            }
            offset++
        }
    }
}
